import { Routes } from '@angular/router';
import { HomeComponent } from './main-components/dashboard/home.component';
import { OtpGrantsComponent } from './otpgrants/otpgrants.component';
import { authGuard } from './guards/auth.guard';
import { AuthCallbackComponent } from './main-components/auth-callback/auth-callback.component';
import { NavigationBarComponent } from './main-components/nav-bar/nav-bar.component';
import { ErrorPageComponent } from './shared/components/error-page/error-page.component';
import { Role, TASK_EXECUTION_MIN_ROLE } from '../enums/enums';

export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./root-redirect.component').then((c) => c.RootRedirectComponent),
    pathMatch: 'full',
  },

  {
    path: 'login',
    title: 'Log in',
    loadComponent: () => import('./main-components/login/mslogin').then((c) => c.LoginComponent),
  },
  { path: 'auth', component: AuthCallbackComponent },
  {
    path: '',
    component: NavigationBarComponent,
    children: [
      {
        path: 'error',
        component: ErrorPageComponent,
      },
      {
        path: 'access-denied',
        title: 'Access Denied',
        loadComponent: () =>
          import('./main-components/no-access-page/access-denied-page.component').then(
            (m) => m.AccessDeniedComponent,
          ),
      },
      {
        path: 'app',
        title: 'Dashboard',
        canMatch: [authGuard],
        data: { minRole: Role.ViewOnly },
        component: HomeComponent,
      },
      {
        path: 'message-queue',
        title: 'Message Queue',
        canMatch: [authGuard],
        data: { minRole: Role.ViewOnly },
        loadComponent: () =>
          import('./main-components/message-queue/message-queue.component').then(
            (m) => m.MessageQueueComponent,
          ),
      },
      {
        path: 'customers',
        title: 'Customers',
        canMatch: [authGuard],
        data: { minRole: Role.ViewOnly },
        loadComponent: () =>
          import('./main-components/customers-grid/customers-grid.component').then(
            (m) => m.CustomersGridComponent,
          ),
      },
      {
        path: 'customers/:systemId/access',
        title: 'Task Execution Access',
        canMatch: [authGuard],
        data: { minRole: TASK_EXECUTION_MIN_ROLE },
        loadComponent: () =>
          import('./components/venue-access/venue-access.component').then(
            (m) => m.VenueAccessComponent,
          ),
      },
      {
        path: 'customers/:systemId',
        title: 'Customer Detail',
        canMatch: [authGuard],
        data: { minRole: Role.ViewOnly },
        loadComponent: () =>
          import('./components/customer-detail/customer-detail.component').then(
            (m) => m.CustomerDetailComponent,
          ),
      },
      {
        path: 'integrations',
        title: 'Integrations',
        canMatch: [authGuard],
        data: { minRole: Role.ViewOnly },
        loadComponent: () =>
          import('./main-components/integrations/integrations.component').then(
            (m) => m.IntegrationsComponent,
          ),
      },
      {
        path: 'settings',
        title: 'Settings',
        canMatch: [authGuard],
        data: { adminOnly: true },
        loadComponent: () =>
          import('./main-components/settings/settings.component').then((m) => m.SettingsComponent),
      },
      {
        path: 'user-management',
        title: 'User Management',
        canMatch: [authGuard],
        data: { adminOnly: true },
        loadComponent: () =>
          import('./components/user-management/user-management.component').then(
            (m) => m.UserManagementComponent,
          ),
      },
      {
        path: 'roles-permissions',
        title: 'Roles & Permissions',
        canMatch: [authGuard],
        data: { adminOnly: true },
        loadComponent: () =>
          import('./components/roles-permissions/roles-permissions.component').then(
            (m) => m.RolesPermissionsComponent,
          ),
      },
      {
        path: 'feedback',
        title: 'Feedback',
        canMatch: [authGuard],
        data: { adminOnly: true },
        loadComponent: () =>
          import('./components/feedback-viewer/feedback-viewer.component').then(
            (m) => m.FeedbackViewerComponent,
          ),
      },
      {
        path: 'customer-sets',
        title: 'Customer Sets',
        canMatch: [authGuard],
        data: { adminOnly: true },
        loadComponent: () =>
          import('./components/customer-sets/customer-sets-page/customer-sets.component').then(
            (m) => m.CustomerSetsComponent,
          ),
      },
      {
        path: 'customer-sets-form/:id',
        title: 'Customer Sets Form',
        canMatch: [authGuard],
        data: { adminOnly: true },
        loadComponent: () =>
          import('./components/customer-sets/customer-sets-form/customer-sets-form.component').then(
            (m) => m.CustomerSetsFormComponent,
          ),
      },
      {
        path: 'customer-sets-form',
        title: 'Customer Sets Form',
        canMatch: [authGuard],
        data: { adminOnly: true },
        loadComponent: () =>
          import('./components/customer-sets/customer-sets-form/customer-sets-form.component').then(
            (m) => m.CustomerSetsFormComponent,
          ),
      },
      {
        path: 'support',
        title: 'Support',
        loadComponent: () =>
          import('./main-components/support/support.component').then((m) => m.SupportComponent),
      },
      // {
      //   path: 'otpgrants',
      //   component: OtpGrantsComponent,
      //   title: 'OTP Grants',
      // },
      {
        path: 'alerts',
        title: 'Alerts',
        canMatch: [authGuard],
        data: { minRole: Role.ViewOnly },
        loadComponent: () =>
          import('./components/alerts/alerts.component').then((m) => m.AlertsComponent),
      },
      {
        path: 'snapshots',
        title: 'Snapshots',
        canMatch: [authGuard],
        data: { minRole: Role.ViewOnly },
        loadComponent: () =>
          import('./main-components/snapshots/snapshots.component').then(
            (m) => m.SnapshotsComponent,
          ),
      },
      {
        path: 'snapshots/:snapshotId',
        title: 'Snapshot',
        canMatch: [authGuard],
        data: { minRole: Role.ViewOnly },
        loadComponent: () =>
          import('./main-components/snapshots/snapshots.component').then(
            (m) => m.SnapshotsComponent,
          ),
      },
      {
        path: 'snapshots/:snapshotId/compare/:otherId',
        title: 'Compare Snapshots',
        canMatch: [authGuard],
        data: { minRole: Role.ViewOnly },
        loadComponent: () =>
          import('./main-components/snapshots/snapshots.component').then(
            (m) => m.SnapshotsComponent,
          ),
      },
    ],
  },

  { path: '**', redirectTo: '' },
];
